import React, { useState, useEffect, useCallback, useRef } from 'react';
import Spinner from './Spinner/Spinner';
import {
  Table,
  Input,
  Pagination,
  PaginationItem,
  PaginationLink,
  Button,   
} from 'reactstrap';

import { historicalDataApi } from '../lib/api/historicalDataApi';

const PASTEL_GREEN = '#a7f3d0'; 
const PASTEL_RED = '#fecaca';  
const DEBOUNCE_DELAY = 500;

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const DataTable = ({
  columns,
  fetchData,
  initialPageSize = 10,
  pageSizeOptions = [5, 10, 20, 50, 100],
  defaultSortField = null,
  defaultSortDirection = 'asc',
  showSearch = true,
  showPageSize = true,
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [sortField, setSortField] = useState(defaultSortField);
  const [sortDirection, setSortDirection] = useState(defaultSortDirection);
  const [lastTimeStamp, setLastTimeStamp] = useState("");

  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchTerm = useDebounce(searchInput, DEBOUNCE_DELAY);
  const [searchText, setSearchText] = useState('');
  const [searchColumn, setSearchColumn] = useState('');
  const [navigateType, setNavigateType] = useState('next');

  const [filters, setFilters] = useState({});
  const debouncedFilters = useDebounce(filters, DEBOUNCE_DELAY);

  useEffect(() => {
    setSearchText(debouncedSearchTerm);
    setCurrentPage(1); 
  }, [debouncedSearchTerm]);

  const fetchTableData = useCallback(async () => {
    if (loading || !fetchData) {
        return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = {
        page: currentPage,
        pageSize: pageSize || 10,
        sortField,
        sortDirection,
        filters: debouncedFilters
      };

      const response = await fetchData(params);

      if (response && Array.isArray(response.data) && typeof response?.totalCount === 'number') {
        setData(response.data);
        setTotalCount(response.totalCount);
      } else {
         console.error("Invalid data structure received from fetchData:", response);
         throw new Error("Received invalid data format from the server.");
      }

    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to fetch data.');
      setData([]);
      setTotalCount(0);
    } finally {
      setLoading(false); 
    }

  }, [currentPage, pageSize, sortField, sortDirection, debouncedFilters, fetchData]); // Update dependency array

  useEffect(() => {
    fetchTableData();
  }, [fetchTableData]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
        setCurrentPage(page);
    }
  };

  const handleSort = (field) => {
     if (!columns.find(col => col.dataField === field)?.sortable) return;

     console.log(field);
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); 
  };

  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };
  const handleSearchColumnChange = (e) => {
    setSearchColumn(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      if (value === '' || value === null || value === undefined) {
        delete newFilters[field];
      } else {
        newFilters[field] = value;
      }
      return newFilters;
    });
    setCurrentPage(1); 
  };

   const handlePageSizeChange = (e) => {
     const newSize = Number(e.target.value);
     setPageSize(newSize);
     setCurrentPage(1); 
   }

  const resetFilters = () => {
    setFilters({});
    setCurrentPage(1);
  };

  const renderSortIndicator = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? ' ▲' : ' ▼'; 
  };

  const renderFilterInput = (column) => {
    if (!column.filterable) return null;
    const filterValue = filters[column.dataField] ?? ''; 

    switch (column.filterType) {
      case 'text':
        return (
          <Input
            type="text"
            value={filterValue}
            onChange={(e) => handleFilterChange(column.dataField, e.target.value)}
            placeholder={`Filter ${column.text}...`}
            bsSize="sm"
            aria-label={`Filter by ${column.text}`}
          />
        );
      case 'select':
        return (
          <Input
            type="select"
            value={filterValue}
            onChange={(e) => handleFilterChange(column.dataField, e.target.value)}
            bsSize="sm"
             aria-label={`Filter by ${column.text}`}
          >
            <option value="">All</option>
            {column.filterOptions?.map(option => ( 
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Input>
        );
      case 'number':
        return (
          <Input
            type="number"
            value={filterValue}
            onChange={(e) => handleFilterChange(column.dataField, e.target.value)}
            placeholder={`Filter ${column.text}...`}
            bsSize="sm"
            aria-label={`Filter by ${column.text}`}
          />
        );

      default:
         console.warn(`Unsupported filter type: ${column.filterType} for column ${column.dataField}`);
        return null;
    }
  };

  const totalPages = pageSize === -1 || totalCount === 0 ? 1 : Math.ceil(totalCount / pageSize);
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = pageSize === -1 ? totalCount : Math.min(currentPage * pageSize, totalCount);

  const renderPagination = () => {
    if (pageSize === -1 || totalPages <= 1) {
      return null;
    }
  
    return (
      <>
        <PaginationItem disabled={currentPage === 1}>
          <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} aria-label="Previous Page">
            Back
          </PaginationLink>
        </PaginationItem>
        <PaginationItem disabled={currentPage === totalPages}>
          <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} aria-label="Next Page">
            Next
          </PaginationLink>
        </PaginationItem>
      </>
    );
  };
  
  return (
    <div className="data-table-container">
      {Object.keys(filters).length > 0 && (
        <div className="mb-3">
          <Button color="secondary" size="sm" onClick={resetFilters}>
            Reset Filters
          </Button>
        </div>
      )}
      <div className="table-responsive">
        <Table striped hover responsive>
          <thead>
            <tr>
              {columns.map(column => (
                <th key={column.dataField} style={column.headerStyle || {}}>
                  <div className="d-flex flex-column">
                    <span
                      onClick={() => column.sortable && handleSort(column.dataField)}
                      style={column.sortable ? { cursor: 'pointer' } : {}}
                      className="font-weight-bold text-nowrap"
                      role={column.sortable ? 'button' : undefined}
                      tabIndex={column.sortable ? 0 : undefined}
                      onKeyPress={(e) => column.sortable && (e.key === 'Enter' || e.key === ' ') && handleSort(column.dataField)} 
                    >
                      {column.text}
                      {column.sortable && renderSortIndicator(column.dataField)}
                    </span>

                    {column.filterable && (
                      <div className="mt-1">
                        {renderFilterInput(column)}
                      </div>
                    )}
                  </div>
              </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-4">
                  <Spinner color="primary" />
                  <span className="ml-2">Loading...</span>
                </td>
              </tr>
            ) : data.length > 0 ? (
              data.map((row, rowIndex) => (
                <tr key={row.id ?? rowIndex}>
                  {columns.map(column => (
                    <td key={`${row.id ?? rowIndex}-${column.dataField}`}>
                      {column.formatter
                        ? column.formatter(row[column.dataField], row) 
                        : row[column.dataField] ?? 'N/A'} 
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="text-center py-4 text-muted">
                    {error ? 'Error loading data' : 'No records found'}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {!loading && totalCount > 0 && (
        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-3">
          <div className="text-muted mb-2 mb-sm-0">
            Showing {pageSize === -1 ? `all ${totalCount}` : `${startItem} to ${endItem} of ${totalCount}`} entries
          </div>
          {totalPages > 1 && pageSize !== -1 && (
              <Pagination aria-label="Table navigation">
                {renderPagination()}
              </Pagination>
          )}
        </div>
      )}
    </div>
  );
};


const HistoricalDataTableModal = ({ deviceId }) => {
  const fetchHistoricalData = useCallback(async (params) => {
    const metricData = await historicalDataApi.fetchHistoricalDataWithPagination({...params, deviceId});
    return metricData
  }, [deviceId]); 

  const columns = [
    {
      dataField: 'timestamp',
      text: 'Timestamp',
      sortable: true,
      filterable: true,
      filterType: 'text', 
      formatter: (cell) => cell ? new Date(cell).toLocaleString() : 'N/A', 
      headerStyle: { minWidth: '200px' }
    },
    {
      dataField: 'primaryConnection',
      text: 'Interface',
      sortable: true,
      filterable: true,
      filterType: 'text', 
      formatter: (cell) => cell ? cell : 'N/A', 
      headerStyle: { minWidth: '90px' }
    },
    {
      dataField: 'powerState',
      text: 'Power State',
      sortable: true,
      filterable: true,
      filterType: 'text', 
      formatter: (cell) => cell ? cell : 'N/A', 
      headerStyle: { minWidth: '90px' }
    },
    {
      dataField: 'signalStrength',
      text: 'Signal (dBm)',
      sortable: true,
      filterable: true,
      filterType: 'number',
      formatter: (cell) => (typeof cell === 'number' ? cell : 'N/A'),
      headerStyle: { minWidth: '90px' }
    },
    {
      dataField: 'cpuTempCelsius',
      text: 'CPU (°C)',
      sortable: true,
      filterable: true,
      filterType: 'number',
      formatter: (cell) => (typeof cell === 'number' ? cell.toFixed(1) : 'N/A'),
       headerStyle: { minWidth: '100px' }
    },
    {
      dataField: 'lanLatencyMs',
      text: 'LAN (ms)',
      sortable: true,
      filterable: true,
      filterType: 'number',
      formatter: (cell) => (typeof cell === 'number' ? cell : 'N/A'),
       headerStyle: { minWidth: '100px' }
    },
    {
      dataField: 'devicePowerW',
      text: 'Power (W)',
      sortable: true,
      filterable: true,
      filterType: 'number',
      formatter: (cell) => (typeof cell === 'number' ? cell.toFixed(2) : 'N/A'),
      headerStyle: { minWidth: '110px' }
    },
    {
      dataField: 'uptimeSeconds',
      text: 'Uptime (s)',
      sortable: true,
      filterable: true,
      filterType: 'number',
      formatter: (cell) => (typeof cell === 'number' ? cell : 'N/A'),
      headerStyle: { minWidth: '120px' }
    },
    {
      dataField: 'apiOnline',
      text: 'Box Api',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [ { value: 'true', label: 'Yes' }, { value: 'false', label: 'No' } ],
      formatter: (cell) => (
        <span style={{
            backgroundColor: cell ? PASTEL_GREEN : PASTEL_RED,
            padding: '2px 6px',
            borderRadius: '4px',
            fontWeight: 'bold',
            color: '#333' 
         }}>
          {cell ? 'Yes' : 'No'}
        </span>
      ),
       headerStyle: { minWidth: '80px', textAlign: 'center' } 
    },
    {
      dataField: 'registered',
      text: 'Reg.',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [ { value: 'true', label: 'Yes' }, { value: 'false', label: 'No' } ],
       formatter: (cell) => (
         <span style={{
            backgroundColor: cell ? PASTEL_GREEN : PASTEL_RED,
            padding: '2px 6px',
            borderRadius: '4px',
            fontWeight: 'bold',
            color: '#333'
         }}>
          {cell ? 'Yes' : 'No'}
        </span>
      ),
       headerStyle: { minWidth: '80px', textAlign: 'center' }
    }
  ];

  return (
        <DataTable
          columns={columns}
          fetchData={fetchHistoricalData} 
          initialPageSize={10}
          pageSizeOptions={[5, 10, 25, 50, 100]} 
          defaultSortField="timestamp"
          defaultSortDirection="desc"
          showSearch={true} 
          showPageSize={true} 
        />
  );
};


export default HistoricalDataTableModal;
